import * as React from 'react'

import { cn } from '@/lib/utils'

/**
 * Spec 07 · Quantity stepper.
 *
 * Hairline box at radius 3 with 6/12 padding; 28px square controls at
 * radius 3 with a 28%-ink border. At 1 the minus is disabled — faint
 * border, no pointer — rather than hidden, so the control doesn't
 * reflow as the count changes.
 */
interface QuantityStepperProps {
  value: number
  onChange: (value: number) => void
  min?: number
  max?: number
  labels?: { decrease: string; increase: string }
  className?: string
  disabled?: boolean
}

function QuantityStepper({
  value,
  onChange,
  min = 1,
  max = Infinity,
  labels = { decrease: 'Decrease quantity', increase: 'Increase quantity' },
  className,
  disabled,
}: QuantityStepperProps) {
  const atMin = disabled || value <= min
  const atMax = disabled || value >= max

  return (
    <div
      className={cn(
        'inline-flex items-center gap-3.5 rounded-full border border-[color:var(--sss-field-border)] px-3 py-1.5',
        className
      )}
    >
      <StepButton label={labels.decrease} disabled={atMin} onClick={() => onChange(value - 1)}>
        −
      </StepButton>
      <span className="min-w-4 text-center text-[14px] font-semibold tabular-nums">{value}</span>
      <StepButton label={labels.increase} disabled={atMax} onClick={() => onChange(value + 1)}>
        +
      </StepButton>
    </div>
  )
}

function StepButton({
  label,
  disabled,
  onClick,
  children,
}: {
  label: string
  disabled?: boolean
  onClick: () => void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      aria-label={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex size-7 items-center justify-center rounded-full bg-transparent text-[16px] leading-none',
        'transition-colors duration-[120ms] ease-standard',
        'focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_var(--sss-bg),0_0_0_4px_var(--sss-ring)]',
        disabled
          ? 'cursor-not-allowed text-ink-faint'
          : 'cursor-pointer text-ink-muted hover:text-ink'
      )}
    >
      {children}
    </button>
  )
}

export { QuantityStepper }
